import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {ListEmptyState} from '@/components/section-card'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {
  useBitacora,
  BITACORA_PAGE_SIZE,
  type BateriaBitacoraEntrada,
  type BateriaBitacoraTipo,
} from '@/hooks/bateria/use-bateria-tandas'
import {tope} from '@/lib/campos-texto'

const dateFormat = new Intl.DateTimeFormat('es-CO', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

// Un hecho ocurrido no espera a nadie ni tiene magnitud: todo `neutral` (U2) y solo lo terminal `ink`.
const TIPO_TONE: Record<BateriaBitacoraTipo, 'neutral' | 'ink'> = {
  creacion: 'neutral',
  alta_paquete: 'neutral',
  retiro: 'neutral',
  reasignacion: 'neutral',
  ingesta: 'neutral',
  revision: 'neutral',
  cierre: 'ink',
  entrega: 'ink',
  identidad_corregida: 'neutral',
  forma_cambiada: 'neutral',
  validacion_retirada: 'neutral',
  hoja_descartada: 'neutral',
  hoja_restaurada: 'neutral',
  tanda_descartada: 'neutral',
  confirmaciones_revertidas: 'neutral',
}

// Orden del glosario (la vida del lote, correcciones al final), no alfabético.
const TIPOS: BateriaBitacoraTipo[] = [
  'creacion',
  'alta_paquete',
  'ingesta',
  'tanda_descartada',
  'revision',
  'hoja_descartada',
  'hoja_restaurada',
  'retiro',
  'reasignacion',
  'identidad_corregida',
  'forma_cambiada',
  'validacion_retirada',
  'confirmaciones_revertidas',
  'cierre',
  'entrega',
]

function BitacoraRow({entrada}: {entrada: BateriaBitacoraEntrada}) {
  const {t} = useTranslation()
  return (
    <li className="surface-divider flex items-start gap-3 border-b py-3 last:border-0" data-testid="bitacora-entrada">
      <Tag tone={TIPO_TONE[entrada.tipo]}>{t(`admin.bateria.bitacora.tipo.${entrada.tipo}`)}</Tag>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground">{entrada.detalle}</p>
        <p className="mt-0.5 font-mono text-xs text-muted-foreground">
          {dateFormat.format(new Date(entrada.cuando))}
          {entrada.quien && <> · {entrada.quien}</>}
        </p>
      </div>
    </li>
  )
}

/** P8/G13. El orden descendente es del servidor; la búsqueda hace de filtro por persona. */
export function BateriaBitacora({
  businessId,
  evaluacionId,
  enabled,
}: {
  businessId: string | undefined
  evaluacionId: string | undefined
  enabled: boolean
}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const [tipo, setTipo] = useState<BateriaBitacoraTipo | ''>('')
  const [searchInput, setSearchInput] = useState('')
  const search = useDebouncedValue(searchInput, 300)
  const {data, isLoading} = useBitacora(businessId, evaluacionId, enabled, {page, tipo, search})

  const entradas = data?.items ?? []
  const total = data?.total ?? 0
  const filtrando = tipo !== '' || search !== ''

  return (
    <section className="surface overflow-hidden" data-testid="bitacora">
      <div className="surface-divider flex flex-wrap items-center gap-3 border-b border-dashed px-5 py-3">
        <Input
          maxLength={tope('BUSCA')}
          className="h-8 w-64"
          value={searchInput}
          data-testid="bitacora-buscar"
          placeholder={t('admin.bateria.bitacora.buscarPlaceholder')}
          aria-label={t('admin.bateria.bitacora.buscarPlaceholder')}
          onChange={(e) => {
            setSearchInput(e.target.value)
            setPage(1)
          }}
        />
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
            {t('admin.bateria.bitacora.filtroTipo')}
          </span>
          <select
            data-testid="bitacora-filtro-tipo"
            value={tipo}
            onChange={(e) => {
              setTipo(e.target.value as BateriaBitacoraTipo | '')
              setPage(1)
            }}
            className="h-8 rounded-md border border-input bg-transparent px-2 text-sm text-foreground">
            <option value="">{t('admin.bateria.bitacora.filtroTodos')}</option>
            {TIPOS.map((valor) => (
              <option key={valor} value={valor}>
                {t(`admin.bateria.bitacora.tipo.${valor}`)}
              </option>
            ))}
          </select>
        </label>
        {total > 0 && (
          <span className="ml-auto font-mono text-[11px] text-muted-foreground" data-testid="bitacora-total">
            {t('admin.bateria.bitacora.totalCount', {count: total})}
          </span>
        )}
      </div>

      {isLoading && !data ? (
        <Skeleton className="m-4 h-48" data-testid="bitacora-skeleton" />
      ) : entradas.length === 0 ? (
        <div className="px-5 py-4" data-testid="bitacora-empty">
          <ListEmptyState
            page
            title={filtrando ? t('admin.bateria.bitacora.emptyFiltroTitle') : t('admin.bateria.bitacora.emptyTitle')}
            body={filtrando ? t('admin.bateria.bitacora.emptyFiltroBody') : undefined}
          />
        </div>
      ) : (
        <div className="px-5 pb-3">
          <ul data-testid="bitacora-lista">
            {entradas.map((entrada, i) => (
              // La entrada es derivada y no tiene id: cuando+tipo+índice es estable en esta página.
              <BitacoraRow key={`${entrada.cuando}-${entrada.tipo}-${i}`} entrada={entrada} />
            ))}
          </ul>
          <PaginationBar page={page} pageSize={BITACORA_PAGE_SIZE} total={total} onPageChange={setPage} />
        </div>
      )}
    </section>
  )
}
